"use client";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "@/context/UserContext";
import {
  addPhotoToPersonalCollection,
  getPersonalCollectionById,
  getPersonalCollectionsByUser,
} from "@/services/PersonalCollectionService";
import {
  PersonalCollectionSummary,
  PersonalCollection,
  BasicPersonalCollection,
} from "@/interfaces/personal-collections/personal-collections.interface";
import NewPersonalCollectionModal from "@/components/Profile/NewPersonalCollectionModal/NewPersonalCollectionModal";
import { PhotoI } from "@/interfaces/galleries/Galleries";
import { notifyError, notifySuccess } from "@/utils/NotificationFunctions";

type Props = {
  open: boolean;
  onClose: () => void;
  photoId: string;
};

export default function AddToCollectionModal({ open, onClose, photoId }: Props) {
  const { currentUser, userToken } = useUserContext();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collections, setCollections] = useState<PersonalCollectionSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [selected, setSelected] = useState<PersonalCollection | null>(null);
  const photos = useMemo<PhotoI[]>(() => selected?.photos || [], [selected]);
  const [position, setPosition] = useState<number | undefined>(undefined);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const refreshCollections = async (prefSelectId?: string) => {
    if (!currentUser?._id) return;
    try {
      const res = await getPersonalCollectionsByUser(currentUser._id, userToken);
      const arr = res && res.status === "Success" && Array.isArray(res.data) ? res.data : [];
      setCollections(arr);
      if (arr.length) {
        setSelectedId(prefSelectId || arr[0].id);
      } else {
        setSelectedId("");
      }
    } catch {
      setCollections([]);
      setSelectedId("");
    }
  };

  useEffect(() => {
    if (!open) return;
    const run = async () => {
      if (!currentUser?._id) return;
      setLoading(true);
      try {
        await refreshCollections();
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [open, currentUser?._id]);

  useEffect(() => {
    const fetchOne = async () => {
      if (!selectedId) {
        setSelected(null);
        return;
      }
      try {
        const res = await getPersonalCollectionById(selectedId);
        if (res && res.status === "Success") {
          setSelected(res.data);
          // default insert after last item (append)
          const len = res.data.photos.length;
          setPosition(len > 0 ? len : undefined);
        } else {
          setSelected(null);
        }
      } catch {
        setSelected(null);
      }
    };
    if (open) fetchOne();
  }, [selectedId, open]);

  if (!open) return null;

  const handleClose = () => {
    if (saving) return;
    setCollections([]);
    setSelectedId("");
    setSelected(null);
    setPosition(undefined);
    onClose();
  };

  const handleAdd = async () => {
    if (!currentUser?._id) {
      notifyError("Please log in to use collections.");
      return;
    }
    if (!selectedId) {
      notifyError("Please select a collection");
      return;
    }
    setSaving(true);
    try {
      const payload: { photoId: string; position?: number } = { photoId };
      if (photos.length > 0 && position !== undefined) payload.position = position;
      const res = await addPhotoToPersonalCollection(selectedId, payload, userToken);
      if (res && res.status === "Success") {
        notifySuccess("Photo added to collection");
        handleClose();
      } else {
        notifyError("Failed to add photo");
      }
    } catch (e) {
      notifyError("Connection error");
    } finally {
      setSaving(false);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      <div className="relative w-full max-w-6xl rounded-xl bg-[#23262f] text-white shadow-xl ring-1 ring-white/10">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold">Add to collection</h2>
          <button
            onClick={handleClose}
            className="px-2 py-1 text-sm rounded-md hover:bg-white/10"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-white/80">
            Compose your own galleries, including various personalities across all the categories and eras. To
            add photos, go to any existing gallery, open the target photo and tap ‘+ to My collection’. You
            can make your collections publicly visible at your profile, or keep it private and share the link
            with your companions. By default your collections are available only to those who has a link,
            provided by you. To make it private again, duplicate the gallery and delete the old one
          </p>

          {!currentUser?._id ? (
            <p className="text-sm text-red-300">You must be logged in to add photos to a collection.</p>
          ) : (
            <>
              {collections.length === 0 ? (
                <p className="text-sm text-white/70">You don't have any collections yet.</p>
              ) : (
                <div>
                  <label className="block text-sm mb-1">Add to this collection:</label>
                  <select
                    className="w-full rounded-md border border-white/20 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                  >
                    {collections.map((c) => (
                      <option key={c.id} className="bg-[#23262f]" value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {photos.length > 0 && (
                <div>
                  <label className="block text-sm mb-1">Put the new photo after photo #</label>
                  <select
                    className="w-full rounded-md border border-white/20 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                    value={position ?? photos.length}
                    onChange={(e) => setPosition(parseInt(e.target.value, 10))}
                  >
                    {photos.map((p, i) => (
                      <option key={p._id} className="bg-[#23262f]" value={i + 1}>
                        {i + 1} - {p.name} - {p.year}
                      </option>
                    ))}
                    <option className="bg-[#23262f]" value={photos.length}>
                      {photos.length} - (end)
                    </option>
                  </select>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-white/10">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm bg-white/10 hover:bg-white/15"
          >
            Create a new Collection
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm bg-white/10 hover:bg-white/15"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm text-white bg-[linear-gradient(137.48deg,rgba(255,83,188,0.25)_15.13%,rgba(10,252,212,0.25)_87.01%)] hover:opacity-90 disabled:opacity-60"
              disabled={saving || !currentUser?._id || !selectedId}
            >
              {saving ? "Adding..." : "Add"}
            </button>
          </div>
        </div>
      </div>
      <NewPersonalCollectionModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={(c: BasicPersonalCollection) => {
          setShowCreateModal(false);
          // Prefer selecting the newly created collection
          refreshCollections(c.id);
        }}
      />
    </div>
  );

  return typeof window !== "undefined" ? createPortal(modal, document.body) : null;
}
